import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const COLLAPSE_KEY = "pulso-admin.sidebar-collapsed";

/** Which create dialog the layout should render open, if any. */
export type QuickAddKind = "event" | "parent-event" | "category" | "venue" | "movie" | null;

interface LayoutContextValue {
  /** Sidebar collapsed to icon-only rail (desktop). */
  collapsed: boolean;
  toggleCollapsed: () => void;
  /** Mobile slide-over open state. */
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  /** Command palette (⌘K) open state. */
  commandOpen: boolean;
  setCommandOpen: (open: boolean) => void;
  /** Which create dialog is open (driven by the top-bar "+ Dodaj" menu / ⌘K). */
  quickAdd: QuickAddKind;
  setQuickAdd: (kind: QuickAddKind) => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export function LayoutProvider({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState<QuickAddKind>(null);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore persistence failures (private mode etc.) */
      }
      return next;
    });
  }, []);

  // ⌘K / Ctrl-K toggles the command palette globally.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo(
    () => ({
      collapsed,
      toggleCollapsed,
      mobileOpen,
      setMobileOpen,
      commandOpen,
      setCommandOpen,
      quickAdd,
      setQuickAdd,
    }),
    [collapsed, toggleCollapsed, mobileOpen, commandOpen, quickAdd],
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLayout(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error("useLayout must be used within LayoutProvider");
  return ctx;
}
